import {
  MissingRecordError,
  RevisionConflictError,
  type AnnotationRecord,
  type AnnotationRepository,
} from "@/domain";

import type { DatabaseInvalidationPublisher } from "./broadcast-channel";
import type { MarginaliaDatabase } from "./database";
import { toStorageWriteError } from "./storage-errors";

function assertAnnotationRecord(record: AnnotationRecord): void {
  if (record.pageIndex !== record.target.pageIndex) {
    throw new TypeError(
      "Annotation pageIndex must match its target pageIndex.",
    );
  }

  if (!Number.isInteger(record.revision) || record.revision < 1) {
    throw new TypeError("Annotation revision must be a positive integer.");
  }
}

export class DexieAnnotationRepository implements AnnotationRepository {
  constructor(
    private readonly db: MarginaliaDatabase,
    private readonly invalidation?: DatabaseInvalidationPublisher,
  ) {}

  get(id: string): Promise<AnnotationRecord | undefined> {
    return this.db.annotations.get(id);
  }

  listForDocument(documentId: string): Promise<AnnotationRecord[]> {
    return this.db.annotations
      .where("documentId")
      .equals(documentId)
      .filter((annotation) => annotation.deletedAt === null)
      .toArray();
  }

  listForDocumentIncludingDeleted(
    documentId: string,
  ): Promise<AnnotationRecord[]> {
    return this.db.annotations.where("documentId").equals(documentId).toArray();
  }

  listForPage(
    documentId: string,
    pageIndex: number,
  ): Promise<AnnotationRecord[]> {
    return this.db.annotations
      .where("[documentId+pageIndex]")
      .equals([documentId, pageIndex])
      .filter((annotation) => annotation.deletedAt === null)
      .toArray();
  }

  async create(record: AnnotationRecord): Promise<void> {
    assertAnnotationRecord(record);
    if (record.revision !== 1) {
      throw new TypeError("A new annotation must begin at revision 1.");
    }

    try {
      await this.db.annotations.add(record);
    } catch (error) {
      throw toStorageWriteError(error, "create an annotation");
    }

    this.publishChange(record);
  }

  async update(
    record: AnnotationRecord,
    expectedRevision: number,
  ): Promise<AnnotationRecord> {
    assertAnnotationRecord(record);

    let updated: AnnotationRecord;

    try {
      updated = await this.db.transaction(
        "rw",
        this.db.annotations,
        async () => {
          const current = await this.db.annotations.get(record.id);
          if (current === undefined) {
            throw new MissingRecordError("Annotation", record.id);
          }

          if (current.revision !== expectedRevision) {
            throw new RevisionConflictError(
              record.id,
              expectedRevision,
              current.revision,
            );
          }

          if (current.documentId !== record.documentId) {
            throw new TypeError(
              "An annotation cannot be moved to another document.",
            );
          }

          const next: AnnotationRecord = {
            ...record,
            createdAt: current.createdAt,
            revision: current.revision + 1,
          };
          await this.db.annotations.put(next);
          return next;
        },
      );
    } catch (error) {
      if (
        error instanceof RevisionConflictError ||
        error instanceof MissingRecordError ||
        error instanceof TypeError
      ) {
        throw error;
      }

      throw toStorageWriteError(error, "update an annotation");
    }

    this.publishChange(updated);
    return updated;
  }

  async softDelete(
    id: string,
    expectedRevision: number,
    deletedAt: string,
  ): Promise<void> {
    let deleted: AnnotationRecord;

    try {
      deleted = await this.db.transaction(
        "rw",
        this.db.annotations,
        async () => {
          const current = await this.db.annotations.get(id);
          if (current === undefined) {
            throw new MissingRecordError("Annotation", id);
          }

          if (current.revision !== expectedRevision) {
            throw new RevisionConflictError(
              id,
              expectedRevision,
              current.revision,
            );
          }

          const next: AnnotationRecord = {
            ...current,
            revision: current.revision + 1,
            updatedAt: deletedAt,
            deletedAt,
          };
          await this.db.annotations.put(next);
          return next;
        },
      );
    } catch (error) {
      if (
        error instanceof RevisionConflictError ||
        error instanceof MissingRecordError
      ) {
        throw error;
      }

      throw toStorageWriteError(error, "delete an annotation");
    }

    this.publishChange(deleted);
  }

  private publishChange(record: AnnotationRecord): void {
    this.invalidation?.publish({
      type: "annotation-changed",
      documentId: record.documentId,
      annotationId: record.id,
    });
  }
}
